// src/components/crm/ProspectRelances.jsx
//
// Section "Relances planifiées" intégrée dans la page détail d'un prospect.
// (Le bouton "Nouvel échange" a été déplacé directement dans prospects.js)
//
import React, { useEffect, useState, useCallback } from 'react';
import '../../styles/crm/relances.css';
import RelanceModal from './RelanceModal';
import {
  getProspectRelances,
  createRelance,
  deleteRelance,
  actionOk,
  STATUT_CONFIG,
} from '../../services/crm/relancesService';
import api from '../../services/api';

// ── Options niveau d'intérêt ────────────────────────────────────────────────
const NOTES_OPTIONS = [
  { value: '',      label: '— Non renseigné —' },
  { value: 'chaud', label: '🔥 Très intéressé' },
  { value: 'moyen', label: '🤔 Intéressé moyen' },
  { value: 'froid', label: '❄️ Pas intéressé' },
  { value: 'perdu', label: '🚫 Perdu' },  // ✅ AJOUTÉ
];

const TYPE_ECHANGE_OPTIONS = [
  { value: 'appel',   label: 'Appel téléphonique' },
  { value: 'email',   label: 'Email' },
  { value: 'message', label: 'Message WhatsApp' },
  { value: 'rdv',     label: 'Rendez-vous' },
  { value: 'autre',   label: 'Autre' },
];

export default function ProspectRelances({
  prospectId,
  prospectNom,
  formations_list = [],
  onHistoriqueUpdated,
}) {
  const [relances,     setRelances]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [savingId,     setSavingId]     = useState(null);
  const [showModal,    setShowModal]    = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  // ── popup "appel effectué" (action OK) ──────────────────────────────────
  const [showOkPrompt,    setShowOkPrompt]    = useState(false);
  const [okTypeEchange,   setOkTypeEchange]   = useState('appel');
  const [okNotes,         setOkNotes]         = useState('');
  const [okContenu,       setOkContenu]       = useState('');
  const [pendingOkId,     setPendingOkId]     = useState(null);

  // ── popup "reprogrammer ?" après action OK ──────────────────────────────
  const [reprogModal,  setReprogModal]  = useState(false);

  // ── popup confirmation suppression ────────────────────────────────────
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // ── chargement initial ──────────────────────────────────────────────────
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getProspectRelances(prospectId);
      setRelances(data);
    } catch (err) {
      console.error('Erreur chargement relances :', err);
    } finally {
      setLoading(false);
    }
  }, [prospectId]);

  useEffect(() => { load(); }, [load]);

  // ── Créer une relance ───────────────────────────────────────────────────
  const handleCreate = async ({ dateRelance, commentaire, formationIds }) => {
    try {
      setModalLoading(true);
      const created = await createRelance(prospectId, {
        dateRelance,
        commentaire,
        formationIds: formationIds && formationIds.length > 0 
          ? formationIds 
          : (formations_list[0]?.id ? [formations_list[0].id] : []),
      });
      setRelances((prev) => [created, ...prev]);
      setShowModal(false);
    } catch (err) {
      console.error('Erreur création relance :', err);
    } finally {
      setModalLoading(false);
    }
  };

  // ── Supprimer une relance ───────────────────────────────────────────────
  const handleDelete     = (id) => setDeleteConfirmId(id);

  const confirmDelete = async () => {
    try {
      await deleteRelance(deleteConfirmId);
      setRelances((prev) => prev.filter((r) => r.id !== deleteConfirmId));
    } catch (err) {
      console.error('Erreur suppression relance :', err);
    } finally {
      setDeleteConfirmId(null);
    }
  };

  // ── Ouvrir popup OK ─────────────────────────────────────────────────────
  const openOkPrompt = (id) => {
    setPendingOkId(id);
    setOkTypeEchange('appel');
    setOkNotes('');
    setOkContenu('');
    setShowOkPrompt(true);
  };

  // ── Confirmer appel effectué ────────────────────────────────────────────
  const confirmOk = async () => {
    if (!pendingOkId) return;
    try {
      setSavingId(pendingOkId);
      setShowOkPrompt(false);

      const { relance: updated } = await actionOk(pendingOkId, {
        type_echange: okTypeEchange,
        notes:        okNotes,
        contenu:      okContenu,
      });

      setRelances((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r))
      );

      if (onHistoriqueUpdated) onHistoriqueUpdated();
      setReprogModal(true);
    } catch (err) {
      console.error('Erreur action OK :', err);
    } finally {
      setSavingId(null);
      setPendingOkId(null);
    }
  };

  // ── Reprogrammer après OK ────────────────────────────────────────────────
  const handleReprog = async ({ dateRelance, commentaire, formationIds }) => {
    try {
      setModalLoading(true);
      const created = await createRelance(prospectId, {
        dateRelance,
        commentaire,
        formationIds: formationIds && formationIds.length > 0 
          ? formationIds 
          : (formations_list[0]?.id ? [formations_list[0].id] : []),
      });
      setRelances((prev) => [created, ...prev]);
      setReprogModal(false);
    } catch (err) {
      console.error('Erreur reprogrammation :', err);
    } finally {
      setModalLoading(false);
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('fr-FR') : '';

  if (loading) return <div className="relance-loading">Chargement...</div>;

  return (
    <div className="prospect-relances-section">
      {/* ════════════════════════════════════════════════════════════════════════
          SECTION : RELANCES PLANIFIÉES
      ════════════════════════════════════════════════════════════════════════ */}
      <div className="relances-subsection">
        <div className="relances-header">
          <h4 className="relances-title">
            <i className="fa-solid fa-bell"></i> Relances planifiées
          </h4>
          <button
            className="btn-add-relance"
            onClick={() => setShowModal(true)}
            title="Programmer une nouvelle relance"
          >
            <i className="fa-solid fa-plus"></i> Ajouter une relance
          </button>
        </div>

        {relances.length === 0 ? (
          <div className="empty-state">
            <i className="fa-solid fa-inbox"></i>
            <p>Aucune relance programmée.</p>
          </div>
        ) : (
          <div className="relances-list">
            {relances.map((r) => {
              const cfg = STATUT_CONFIG[r.statutCalc] || STATUT_CONFIG.a_venir;
              return (
                <div key={r.id} className={`relance-card ${cfg.cardCls}`}>
                  <div className="relance-card-header">
                    <div className="relance-date-badge">
                      <span className={`badge ${cfg.badgeCls}`}>{cfg.label}</span>
                      <span className="relance-date">{fmtDate(r.dateRelance)}</span>
                    </div>
                    {r.statutCalc !== 'fait' && (
                      <button
                        className="btn-relance-ok"
                        onClick={() => openOkPrompt(r.id)}
                        disabled={savingId === r.id}
                        title="Marquer cette relance comme effectuée"
                      >
                        {savingId === r.id ? (
                          <i className="fa-solid fa-spinner fa-spin"></i>
                        ) : (
                          <i className="fa-solid fa-check-double"></i>
                        )}
                      </button>
                    )}
                  </div>

                  <div className="relance-card-body">
                    {r.formationNom && (
                      <div className="relance-formation">
                        <i className="fa-solid fa-graduation-cap"></i>{' '}
                        <strong>{r.formationNom}</strong>
                      </div>
                    )}
                    {r.commentaire && (
                      <div className="relance-comment">
                        <p>{r.commentaire}</p>
                      </div>
                    )}
                  </div>

                  <div className="relance-card-footer">
                    <button
                      className="btn-relance-delete"
                      onClick={() => handleDelete(r.id)}
                      title="Supprimer cette relance"
                    >
                      <i className="fa-solid fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          MODAL : Programmer une relance
      ════════════════════════════════════════════════════════════════════════ */}
      <RelanceModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleCreate}
        title="Programmer une relance"
        loading={modalLoading}
        formations={formations_list}
      />

      {/* ════════════════════════════════════════════════════════════════════════
          MODAL : Reprogrammer après OK
      ════════════════════════════════════════════════════════════════════════ */}
      <RelanceModal
        isOpen={reprogModal}
        onClose={() => setReprogModal(false)}
        onSave={handleReprog}
        title="Reprogrammer une relance"
        loading={modalLoading}
        formations={formations_list}
      />

      {/* ════════════════════════════════════════════════════════════════════════
          POPUP : Appel effectué (action OK)
      ════════════════════════════════════════════════════════════════════════ */}
      {showOkPrompt && (
        <div className="relance-modal-overlay" onClick={() => setShowOkPrompt(false)}>
          <div
            className="relance-modal-card ok-prompt-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relance-modal-header">
              <div className="relance-modal-icon ok-icon">
                <i className="fa-solid fa-phone"></i>
              </div>
              <h3 className="relance-modal-title">Appel effectué</h3>
              <button
                className="relance-modal-close"
                onClick={() => setShowOkPrompt(false)}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <div className="relance-modal-body">
              <p className="ok-prompt-info">
                <i className="fa-solid fa-circle-info"></i>{' '}
                Relance pour <strong>{prospectNom}</strong>.
              </p>

              <div className="relance-field">
                <label className="relance-label">
                  <i className="fa-solid fa-phone"></i> Type d'échange
                </label>
                <div className="echange-type-radios">
                  {TYPE_ECHANGE_OPTIONS.map((opt) => (
                    <label key={opt.value} className={`echange-type-radio ${okTypeEchange === opt.value ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="ok-type"
                        value={opt.value}
                        checked={okTypeEchange === opt.value}
                        onChange={() => setOkTypeEchange(opt.value)}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="relance-field">
                <label className="relance-label">
                  <i className="fa-solid fa-fire"></i> Niveau d'intérêt
                  <span className="optional-tag">optionnel</span>
                </label>
                <select
                  className="relance-input"
                  value={okNotes}
                  onChange={(e) => setOkNotes(e.target.value)}
                >
                  {NOTES_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div className="relance-field">
                <label className="relance-label">
                  <i className="fa-regular fa-comment"></i> Résumé de l'échange
                  <span className="optional-tag">optionnel</span>
                </label>
                <textarea
                  className="relance-textarea"
                  placeholder="Ex : Le prospect demande les prix, souhaite rappeler…"
                  rows={3}
                  value={okContenu}
                  onChange={(e) => setOkContenu(e.target.value)}
                />
              </div>
            </div>

            <div className="relance-modal-footer">
              <button
                className="btn-relance-cancel"
                onClick={() => setShowOkPrompt(false)}
              >
                Annuler
              </button>
              <button
                className="btn-relance-save btn-ok"
                onClick={confirmOk}
                disabled={savingId !== null}
              >
                {savingId !== null
                  ? <><i className="fa-solid fa-spinner fa-spin"></i> Enregistrement…</>
                  : <><i className="fa-solid fa-check-double"></i> Confirmer</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal confirmation suppression relance ── */}
      {deleteConfirmId && (
        <div className="relance-modal-overlay" onClick={() => setDeleteConfirmId(null)}>
          <div className="relance-modal-card delete-confirm-card" onClick={(e) => e.stopPropagation()}>
            <div className="relance-modal-header delete-header">
              <div className="relance-modal-icon delete-icon">
                <i className="fa-solid fa-trash"></i>
              </div>
              <h3 className="relance-modal-title">Supprimer la relance</h3>
              <button className="relance-modal-close" onClick={() => setDeleteConfirmId(null)}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="relance-modal-body">
              <p className="delete-confirm-text">
                <i className="fa-solid fa-triangle-exclamation"></i>{' '}
                Êtes-vous sûr de vouloir supprimer cette relance ?<br />
                <span className="delete-confirm-sub">Cette action est irréversible.</span>
              </p>
            </div>
            <div className="relance-modal-footer">
              <button className="btn-relance-cancel" onClick={() => setDeleteConfirmId(null)}>
                Annuler
              </button>
              <button className="btn-relance-delete" onClick={confirmDelete}>
                <i className="fa-solid fa-trash"></i> Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}